import type { BarElement, Plugin } from 'chart.js';

/**
 * Valve overlay of the KNX overview ("Übersicht Ist / Soll"): on every room's Ist bar a thin valve stripe along
 * its bottom inside (a track over the visible bar, the green part = valve %) and the number ("45 %", "≈45 %"
 * while the valve ↔ room mapping is only learned) in the bar, beside the Soll tick and never under it, or
 * right after the bar when it does not fit there.
 *
 * Framework-free and with type imports only, so plain node runs the geometry cases straight from this file
 * (knx-valve-overlay.spec-cases.mjs): `layoutValveLabel` and the colour helpers are pure, the Chart.js plugin
 * only measures, clips to the chart area and paints.
 */

/** The valve green of the room cards' gauge and the charts' valve line. */
export const VALVE_GREEN = '#0ca678';
/** Dark label ink: Bootstrap's body text colour, which is also the dark theme's card surface. */
export const DARK_INK = '#212529';
/** Label font: 10 px semibold, the family of the chart's axis labels (Chart.js default font). */
const FONT_PX = 10;
const FONT_WEIGHT = 600;

/** Geometry in CSS px. */
export const VALVE_GEOMETRY = {
  /** Text ↔ the bar's ends (inside). */
  pad: 5,
  /** Bar end / Soll tick ↔ text placed outside the bar. */
  gap: 4,
  /** Half the Soll tick's stroke (its pointBorderWidth is 3). */
  tickHalf: 1.5,
  /** Soll tick stroke ↔ text inside the bar. */
  tickGap: 4,
  /** Stripe ↔ the bar's ends and its bottom edge. */
  inset: 2,
  stripeH: 3,
  /** Visible bars shorter than this get no stripe (the number still shows, outside). */
  minStripeBar: 12,
} as const;

export interface ValveLabelInput {
  /** The bar's two ends in px, in any order. Chart.js' BarElement.base is the pixel of value 0, here far left
   *  of the chart area (the x-axis starts just below the coldest room, not at 0): both ends are clamped to it. */
  barStart: number;
  barEnd:   number;
  barTop:    number;
  barBottom: number;
  /** Centre of the Soll tick in px, null without a Soll. */
  tickX: number | null;
  /** The label as drawn: advance width, and its ink above / below the baseline (TextMetrics.actualBoundingBox…). */
  textWidth:   number;
  textAscent:  number;
  textDescent: number;
  /** Width the number is placed for (default textWidth): the widest label ("≈100 %"), so every row's number
   *  keeps its place when only the value changes. */
  fitWidth?: number;
  chartLeft:  number;
  chartRight: number;
  /** Valve position 0..100 (clamped again here). */
  pct: number;
  /** Device pixels per CSS px: the stripe's edges and the baseline are snapped to them (default 1). */
  dpr?: number;
  /** Overrides of VALVE_GEOMETRY.pad / .gap. */
  pad?: number;
  gap?: number;
}

/**
 * Where the number goes, first fit wins — for the widest label (ValveLabelInput.fitWidth), so a row's number
 * only moves with the bar, never with the value; for the label's own width only where the widest fits nowhere.
 * Outside the bar it is drawn in the chart's text colour.
 *  - inside:      centred in the bar or, when the Soll tick lies in the bar, in the part left of the tick (as
 *                 wide as Soll − axis minimum whatever Ist is, so the numbers keep their place as Ist changes —
 *                 one column when all rooms share a Soll), in the part right of it only if the left one is too
 *                 narrow; and only if the digits fit between the bar's top and the stripe, ½ px to spare on both
 *                 sides (the overview's 16 px bars leave about 10–11 px for digits 7–8 px high);
 *  - beforeTick:  right after the bar end when a tick further right leaves room (gap on both sides, and within
 *                 the chart area should the tick lie past it) — the number stays at its own bar instead of
 *                 reading as the tick's label;
 *  - outside:     right after max(bar end, tick) + gap;
 *  - outsideLeft: right-aligned before min(bar start, tick) − gap (a bar that does not start at the chart's left
 *                 edge, e.g. one hanging left from 0 °C);
 *  - hidden:      none of these fits (a chart area narrower than about twice the label — not reached at 320 px
 *                 viewports). No number then rather than a clipped one, which would read as another value
 *                 ("≈100 %" cut to "10"); the stripe and the tooltip still show the valve.
 */
export type ValveLabelMode = 'inside' | 'outside' | 'beforeTick' | 'outsideLeft' | 'hidden';

export interface ValveLabelLayout {
  mode: ValveLabelMode;
  textX: number;
  /** Baseline (textBaseline 'alphabetic'): inside centred between the bar's top and the stripe, outside on the bar's middle. */
  textY: number;
  textAlign: 'left' | 'right' | 'center';
  /** Track over the visible bar and its green part from x (0 ≤ fillW ≤ w); null on a visible bar shorter than minStripeBar. */
  stripe: { x: number; y: number; w: number; h: number; fillW: number } | null;
}

/** Valve position clamped to 0..100; null without a usable value (null, undefined, NaN, ±∞). */
export function clampValvePct(pct: number | null | undefined): number | null {
  if (pct == null || !Number.isFinite(pct)) return null;
  return Math.max(0, Math.min(100, pct));
}

/** "45 %" ("≈45 %" for a learned, unconfirmed mapping), rounded to whole percent; null without a usable value. */
export function valveLabel(pct: number | null | undefined, likely: boolean): string | null {
  const p = clampValvePct(pct);
  // Thin space before "%", like the room cards' "45&thinsp;%"
  return p == null ? null : `${likely ? '≈' : ''}${Math.round(p)}\u2009%`;
}

/** Geometry of one room's stripe and number (pure; null on unusable input). */
export function layoutValveLabel(i: ValveLabelInput): ValveLabelLayout | null {
  const g = VALVE_GEOMETRY;
  const pad = i.pad ?? g.pad, gap = i.gap ?? g.gap;
  const nums = [i.barStart, i.barEnd, i.barTop, i.barBottom, i.textWidth, i.textAscent, i.textDescent, i.chartLeft, i.chartRight, i.pct];
  if (nums.some(v => !Number.isFinite(v)) || i.chartRight <= i.chartLeft || i.barBottom <= i.barTop) return null;
  const dpr = i.dpr != null && i.dpr > 0 && Number.isFinite(i.dpr) ? i.dpr : 1;
  const snap = (v: number) => Math.round(v * dpr) / dpr;
  const pct = Math.max(0, Math.min(100, i.pct));

  // Visible bar: never the raw `base` (the pixel of 0 lies outside the chart area).
  const clampX = (v: number) => Math.max(i.chartLeft, Math.min(i.chartRight, v));
  const lo = clampX(Math.min(i.barStart, i.barEnd));
  const hi = clampX(Math.max(i.barStart, i.barEnd));

  let stripe: ValveLabelLayout['stripe'] = null;
  if (hi - lo >= g.minStripeBar) {
    const x = snap(lo + g.inset);
    const w = snap(hi - g.inset) - x;
    const hDev = Math.max(1, Math.round(g.stripeH * dpr));
    const h = hDev / dpr;
    const y = snap(i.barBottom - g.inset) - h;
    // Whole device pixels, in step with the label: "0 %" no green, "100 %" a full track, and a valve shown as
    // 1–99 % at least the track's rounded cap (h / 2) of green and of empty track.
    const cap = Math.ceil(hDev / 2) / dpr;
    const fillW = pct < 0.5 ? 0 : pct >= 99.5 ? w : Math.min(Math.max(cap, w - cap), Math.max(cap, snap(w * pct / 100)));
    stripe = { x, y, w, h, fillW };
  }

  const tickX = i.tickX != null && Number.isFinite(i.tickX) ? i.tickX : null;
  const tickL = tickX != null ? tickX - g.tickHalf : null;
  const tickR = tickX != null ? tickX + g.tickHalf : null;

  // Room for the number inside the bar: the whole bar or, with the tick in the bar, the part left of it, else
  // the part right of it.
  const parts = tickL != null && tickR != null && tickR > lo && tickL < hi
    ? [[lo + pad, Math.min(hi - pad, tickL - g.tickGap)], [Math.max(lo + pad, tickR + g.tickGap), hi - pad]]
    : [[lo + pad, hi - pad]];
  const mid = (top: number, bottom: number) => snap((top + bottom) / 2 + (i.textAscent - i.textDescent) / 2);
  const outsideY = mid(i.barTop, i.barBottom);
  const insideBottom = stripe ? stripe.y : i.barBottom;
  const digitsFit = insideBottom - i.barTop >= i.textAscent + i.textDescent + 1;

  // First fit for a label `tw` wide (see ValveLabelMode); null where none fits.
  const place = (tw: number): ValveLabelLayout | null => {
    const k = digitsFit ? parts.findIndex(([a, b]) => b - a >= tw) : -1;
    if (k >= 0) {
      const [a, b] = parts[k];
      // Right of the tick: left-aligned just after it, so on narrow charts (phones), where the part left of the
      // tick is too short for every room, the numbers form one column at the tick instead of each sitting in
      // the middle of its own bar's remainder.
      return k === 1
        ? { mode: 'inside', textX: a, textY: mid(i.barTop, insideBottom), textAlign: 'left', stripe }
        : { mode: 'inside', textX: (a + b) / 2, textY: mid(i.barTop, insideBottom), textAlign: 'center', stripe };
    }
    if (tickL != null && tickL > hi && hi + gap + tw <= Math.min(tickL - gap, i.chartRight)) {
      return { mode: 'beforeTick', textX: hi + gap, textY: outsideY, textAlign: 'left', stripe };
    }
    const right = Math.max(hi, tickR ?? hi) + gap;
    if (right + tw <= i.chartRight) return { mode: 'outside', textX: right, textY: outsideY, textAlign: 'left', stripe };
    const left = Math.min(lo, tickL ?? lo) - gap;
    if (left - tw >= i.chartLeft) return { mode: 'outsideLeft', textX: left, textY: outsideY, textAlign: 'right', stripe };
    return null;
  };
  // Placed for the widest label, so the number does not move when only the value changes; for the label itself
  // only where the widest fits nowhere.
  const fitWidth = i.fitWidth != null && Number.isFinite(i.fitWidth) ? Math.max(i.textWidth, i.fitWidth) : i.textWidth;
  return place(fitWidth) ?? place(i.textWidth)
    ?? { mode: 'hidden', textX: (lo + hi) / 2, textY: outsideY, textAlign: 'center', stripe };
}

// ── Colours ───────────────────────────────────────────────────────────────────
export interface Rgba { r: number; g: number; b: number; a: number; }

/** "#rgb", "#rrggbb", "rgb(…)" / "rgba(…)" (comma separated) → channels 0..255 and alpha 0..1; null otherwise. */
export function parseColor(css: string): Rgba | null {
  const s = css.trim().toLowerCase();
  let m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(s);
  if (m) {
    const h = m[1].length === 3 ? [...m[1]].map(c => c + c).join('') : m[1];
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16), a: 1 };
  }
  m = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(s);
  if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] != null ? Math.max(0, Math.min(1, +m[4])) : 1 };
  return null;
}

/** `top` composited over the opaque `bottom`. */
function over(top: Rgba, bottom: Rgba): Rgba {
  const mix = (t: number, u: number) => t * top.a + u * (1 - top.a);
  return { r: mix(top.r, bottom.r), g: mix(top.g, bottom.g), b: mix(top.b, bottom.b), a: 1 };
}

function luminance(c: Rgba): number {
  const lin = (v: number) => { v /= 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
}

/** WCAG 2 contrast ratio (1..21) of two colours; translucent ones are composited over `surface` first. */
export function contrastRatio(fg: string, bg: string, surface = '#fff'): number {
  const s = parseColor(surface) ?? { r: 255, g: 255, b: 255, a: 1 };
  const b = parseColor(bg), f = parseColor(fg);
  if (!b || !f) return 1;
  const back = over(b, s);
  const l1 = luminance(over(f, back)), l2 = luminance(back);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/**
 * Ink of a label on `fill` (translucent fills over the card `surface`): white or DARK_INK, whichever contrasts
 * more. On the overview's bars that is ≥ 4.5:1 (WCAG AA for small text) in both themes — white on the light
 * theme's blue / red, DARK_INK on its gray and faint bars; in the dark theme, whose blue / red are lighter,
 * DARK_INK on blue / red and white on gray / faint (cases in knx-valve-overlay.spec-cases.mjs).
 */
export function inkOn(fill: string, surface: string): string {
  return contrastRatio('#fff', fill, surface) >= contrastRatio(DARK_INK, fill, surface) ? '#fff' : DARK_INK;
}

/**
 * Track of the valve stripe on `fill`: on solid bars a groove darker than the bar, so the empty track stays
 * quieter than the green part (a light track on the light theme's blue / red read as a highlight) — black on a
 * light card surface, the dark card surface showing through on a dark one; on a faint bar (alpha < .5, rooms
 * without a Soll), where neither would show, the label ink at low alpha.
 */
export function trackOn(fill: string, surface: string): string {
  const faint = (parseColor(fill)?.a ?? 1) < 0.5;
  const s = parseColor(surface) ?? { r: 255, g: 255, b: 255, a: 1 };
  if (!faint && luminance(s) > 0.5) return 'rgba(0,0,0,.28)';
  const c = faint ? parseColor(inkOn(fill, surface)) ?? s : s;
  return `rgba(${Math.round(c.r)},${Math.round(c.g)},${Math.round(c.b)},${faint ? '.22' : '.55'})`;
}

// ── Chart.js plugin ───────────────────────────────────────────────────────────
/** One room row of the overview, index-aligned with the bar dataset. */
export interface ValveOverlayRow {
  pct: number | null | undefined;
  likely: boolean;
  /** Soll in axis units (the tick), null without one. */
  setpoint: number | null;
  /** Label ink inside the bar and the stripe's track (inkOn / trackOn of the bar's fill). */
  ink: string;
  track: string;
}

/**
 * The stripe: green part and empty track side by side, both clipped to the track's pill (a plain rect where the
 * canvas cannot draw one) — a green part narrower than the stripe is high stays inside the rounded cap.
 */
function drawStripe(ctx: CanvasRenderingContext2D, s: NonNullable<ValveLabelLayout['stripe']>, track: string): void {
  ctx.save();
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(s.x, s.y, s.w, s.h, Math.min(s.h, s.w) / 2);
  else ctx.rect(s.x, s.y, s.w, s.h);
  ctx.clip();
  ctx.fillStyle = VALVE_GREEN;
  ctx.fillRect(s.x, s.y, s.fillW, s.h);
  ctx.fillStyle = track;
  ctx.fillRect(s.x + s.fillW, s.y, s.w - s.fillW, s.h);
  ctx.restore();
}

/**
 * Paints the stripes and numbers right after the bar dataset `datasetIndex` (the Soll ticks, a later dataset,
 * are drawn on top of the stripe), clipped to the chart area so nothing reaches the axis labels.
 */
export function valveOverlayPlugin(o: {
  datasetIndex: number;
  rows: () => readonly ValveOverlayRow[];
  /** Colour of numbers outside the bars: the chart's text colour of the theme. */
  outsideInk: () => string;
}): Plugin<'bar' | 'line'> {
  return {
    id: 'knxValveOverlay',
    afterDatasetDraw: (chart, args) => {
      if (args.index !== o.datasetIndex) return;
      const rows = o.rows();
      const area = chart.chartArea;
      const x = chart.scales['x'];
      if (!rows.length || !area || !x || area.right <= area.left) return;
      const bars = args.meta.data as unknown as BarElement[];
      const ctx = chart.ctx;
      ctx.save();
      ctx.beginPath();
      ctx.rect(area.left, area.top, area.right - area.left, area.bottom - area.top);
      ctx.clip();
      ctx.font = `${FONT_WEIGHT} ${FONT_PX}px ${chart.options.font?.family || 'sans-serif'}`;
      ctx.textBaseline = 'alphabetic';
      // Vertical metrics of the digits, the same for every row ("≈" and "%" stay within them)
      const m = ctx.measureText('0');
      const ascent = m.actualBoundingBoxAscent || FONT_PX * 0.72;
      const descent = m.actualBoundingBoxDescent || 0;
      // Every number is placed for the widest label, so it keeps its place whatever the row's value
      const fitWidth = ctx.measureText(valveLabel(100, true) ?? '').width;
      const dpr = chart.currentDevicePixelRatio || 1;
      for (let i = 0; i < rows.length && i < bars.length; i++) {
        const row = rows[i];
        const text = valveLabel(row.pct, row.likely);
        if (text == null || !bars[i]) continue;
        const { x: head, y, base, height } = bars[i].getProps(['x', 'y', 'base', 'height'], true);
        if (head == null || y == null) continue;
        const l = layoutValveLabel({
          barStart: base, barEnd: head, barTop: y - height / 2, barBottom: y + height / 2,
          tickX: row.setpoint != null ? x.getPixelForValue(row.setpoint) : null,
          textWidth: ctx.measureText(text).width, textAscent: ascent, textDescent: descent, fitWidth,
          chartLeft: area.left, chartRight: area.right, pct: clampValvePct(row.pct) ?? 0, dpr,
        });
        if (!l) continue;
        if (l.stripe) drawStripe(ctx, l.stripe, row.track);
        if (l.mode === 'hidden') continue;
        ctx.fillStyle = l.mode === 'inside' ? row.ink : o.outsideInk();
        ctx.textAlign = l.textAlign;
        ctx.fillText(text, l.textX, l.textY);
      }
      ctx.restore();
    },
  };
}
